import { formatInTimeZone } from "date-fns-tz";
import { safeFilename } from "./safeFilename";

export enum ReportPeriodType {
  // General purpose export over a period of time.
  DateRange = "DateRange",

  // Special Date Ranges that are common, and we format differently to
  // make it easier for the user to identify.
  FinancialYear = "FinancialYear",
  Quarter = "Quarter",
  Month = "Month",

  // Point in time snapshots (not an export for a period of time).
  PointInTime = "PointInTime",
}

export type PointInTimePeriod = {
  type: ReportPeriodType.PointInTime;
  timezone: string;
  date: Date;
};

export type DateRangePeriod = {
  type:
    | ReportPeriodType.DateRange
    | ReportPeriodType.FinancialYear
    | ReportPeriodType.Month
    | ReportPeriodType.Quarter;

  /**
   * User's IANA timezone, e.g. "Australia/Brisbane"
   * User to convert the provided start, end Date's into user's local timezone,
   * so the actual day, month, year printed are correct for the user.
   */
  timezone: string;

  // inclusive of the start date
  // e.g. 2024-07-01 00:00:00.000 for a period starting on 1st July 2024.
  start: Date;

  // exclusive of the end date
  // e.g. 2025-07-01 00:00:00.000 for a period ending on end of day 30 June 2025
  end: Date;
};

export type Period = PointInTimePeriod | DateRangePeriod;

export type ReportUserDetails = {
  fullName?: string; // e.g. Satoshi Nakamoto, or Nakamoto SMSF
  contactEmail?: string; //e.g. satoshi@gmail.com (used if userName is empty)
};

export type BuildReportFileName = {
  user: ReportUserDetails;
  period: Period;

  // standard name for the report type, e.g. "Crypto Tax Report"
  name: string;
};

/**
 * Builds the standardised filename for a report.
 *
 * Explanation:
 * - Syla: comes first for brand identity, particularly for CSV exports which
 * are data only.
 * - Report Type: for user to easily identify which data will be inside.
 * - Date: is designed to be user friendly as main priority.
 * The date formatting will vary based on the type of reporting period used.
 * - User Name: for users with multiple accounts (Accountants) to differentiate
 * between reports.
 */
export function buildReportFileName(report: BuildReportFileName) {
  const brand = "Syla";

  const filename = [
    brand,
    report.name,
    buildReportDate(report.period),
    buildName(report.user),
  ]
    .filter(Boolean)
    .join(" - ")
    .slice(0, 100);

  return safeFilename(filename);
}

function buildName(user: ReportUserDetails) {
  return (
    user.fullName?.trim() ||
    (user.contactEmail || "").replace(/@.*\..+/, "").trim()
  );
}

export type BuildReportShortName = {
  period: Period;
  name: string;
};

export function buildReportShortName(report: BuildReportShortName) {
  return [report.name, buildReportDate(report.period)]
    .filter(Boolean)
    .join(" - ")
    .slice(0, 100);
}

function buildReportDate(period: Period) {
  if (period.type === ReportPeriodType.PointInTime) {
    return formatInTimeZone(period.date, period.timezone, "d LLL yyyy");
  }

  // end dates are always exclusive of the date it's set on, so we subtract 1
  const end = new Date(period.end);
  end.setTime(end.getTime() - 1);

  if (period.type === ReportPeriodType.DateRange) {
    return `${formatInTimeZone(period.start, period.timezone, "d LLL yyyy")} to ${formatInTimeZone(
      end,
      period.timezone,
      "d LLL yyyy",
    )}`;
  }

  switch (period.type) {
    case ReportPeriodType.FinancialYear:
      return `FY ${formatInTimeZone(end, period.timezone, "yyyy")}`;

    case ReportPeriodType.Quarter:
      return `${formatInTimeZone(end, period.timezone, "LLL yyyy")} Quarter`;

    case ReportPeriodType.Month:
      return formatInTimeZone(end, period.timezone, "LLL yyyy");

    default: {
      throw new Error(`Unknown period type ${period}`);
    }
  }
}
